import { ActivityHeader } from '@components/Activity/ActivityHeader'
import { ActivityCardMessage } from '@components/Activity/ActivityCardMessage'
import { ActivityDetails } from '@components/Activity/ActivityDetails'
import { DynamicQuestions } from '@components/Activity/DynamicQuestions'
import { View, Text, YStack, Spinner } from 'tamagui'
import { QuestionFooter } from '@components/Activity/QuestionFooter'
import { useLocalSearchParams } from 'expo-router'
import { useGetActivity } from '@services/api/graphql/activity'
import { useEffect, useMemo, useState } from 'react'
import { useActivityStore } from '@stores/useActivityStore'
import { useForm, FormProvider } from 'react-hook-form'

// Tipo para os dados do formulário
type FormData = {
  [questionId: string]: string | { [assertionId: string]: string }
}

interface IActivityPageProps {
  courseId?: string
  enrollmentId?: string
  activityId?: string
  onPreviousPress?: () => void
  onNextPress?: () => void
  hasPreviousContent?: boolean
  hasNextContent?: boolean
  isLoadingNextOrPreviousLesson?: boolean
  progressCardId: string
  updateEnrollmentProgress: () => void
}

export default function ActivityPage({
  courseId: propsCourseId,
  enrollmentId: propsEnrollmentId,
  activityId: propsActivityId,
  onPreviousPress,
  onNextPress,
  hasPreviousContent,
  hasNextContent,
  isLoadingNextOrPreviousLesson,
  progressCardId,
  updateEnrollmentProgress,
}: IActivityPageProps) {
  // Pega parâmetros da URL ou usa props
  const {
    courseId: paramCourseId,
    enrollmentId: paramEnrollmentId,
    activityId: paramActivityId,
  } = useLocalSearchParams<{
    courseId?: string
    enrollmentId?: string
    activityId?: string
  }>()

  const { setActivity, activity } = useActivityStore()
  const [loading, setLoading] = useState(false)

  const courseId = propsCourseId || paramCourseId
  const enrollmentId = propsEnrollmentId || paramEnrollmentId
  const activityId = propsActivityId || paramActivityId

  // Configuração do formulário
  const methods = useForm<FormData>({
    defaultValues: {},
  })

  // Hook do GraphQL
  const { data, error, getActivity } = useGetActivity({
    courseId: courseId ? Number(courseId) : 0,
    enrollmentId: enrollmentId ? Number(enrollmentId) : 0,
    id: activityId ? Number(activityId) : 0,
  })

  useEffect(() => {
    async function fetchActivity() {
      setLoading(true)
      const { data } = await getActivity({
        variables: {
          courseId: courseId ? Number(courseId) : 0,
          enrollmentId: enrollmentId ? Number(enrollmentId) : 0,
          id: activityId ? Number(activityId) : 0,
        },
      })

      if (data?.activity) {
        setActivity(data.activity)
      }
      setLoading(false)
    }

    fetchActivity()
  }, [courseId, enrollmentId, activityId, getActivity, setActivity])

  const mode = useMemo(() => {
    const hasAnswer = !!activity?.examAnswer
    const canRetry =
      activity?.retryPermitted &&
      activity?.retryCount &&
      activity?.maxAttempts &&
      activity?.retryCount < activity?.maxAttempts

    if (hasAnswer && canRetry) return 'retry'

    if (!hasAnswer || canRetry) return 'edit'

    return 'readonly'
  }, [activity])

  if (loading) {
    return (
      <View flex={1} justifyContent="center" alignItems="center">
        <Spinner size="large" color="$primary-500" />
        <Text mt="$spacing-inset-xs" color="$neutral-600">
          Carregando atividade...
        </Text>
      </View>
    )
  }

  if (error) {
    return (
      <View
        flex={1}
        justifyContent="center"
        alignItems="center"
        p="$spacing-inset-lg"
      >
        <Text color="$error-500" textAlign="center" fontSize="$font-size-md">
          Erro ao carregar atividade
        </Text>
        <Text color="$neutral-600" textAlign="center" mt="$spacing-inset-xs">
          Tente novamente mais tarde
        </Text>
      </View>
    )
  }

  if (!data?.activity) {
    return (
      <View
        flex={1}
        justifyContent="center"
        alignItems="center"
        p="$spacing-inset-lg"
      >
        <Text color="$neutral-600" textAlign="center">
          Atividade não encontrada
        </Text>
      </View>
    )
  }

  const activityTitle = data.activity.title

  return (
    <View gap={'$spacing-inset-lg'}>
      <ActivityHeader
        onPreviousPress={onPreviousPress}
        onNextPress={onNextPress}
        hasPreviousContent={hasPreviousContent}
        hasNextContent={hasNextContent}
        isLoading={isLoadingNextOrPreviousLesson}
      />
      <FormProvider {...methods}>
        <YStack gap={'$spacing-inset-xs'}>
          <Text
            ff="$mulishBold"
            fontSize="$font-size-md"
            color="$neutral-800"
            fontWeight={700}
          >
            {activityTitle}
          </Text>

          {(mode === 'readonly' || mode === 'retry') && (
            <ActivityCardMessage
              progressCardId={progressCardId}
              enrollmentId={enrollmentId || ''}
              getActivity={getActivity}
              mode={mode}
              updateEnrollmentProgress={updateEnrollmentProgress}
            />
          )}
        </YStack>

        <ActivityDetails />

        <DynamicQuestions />
        <QuestionFooter
          progressCardId={progressCardId}
          enrollmentId={enrollmentId || ''}
          getActivity={getActivity}
          mode={mode}
          updateEnrollmentProgress={updateEnrollmentProgress}
        />
      </FormProvider>
    </View>
  )
}
